import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { handwerkerOnboardingGuard } from './core/guards/onboarding.guard';
import { handwerkerRoleGuard } from './core/guards/handwerker-role.guard';
import { endkundeRoleGuard } from './core/guards/endkunde-role.guard';
import { adminRoleGuard } from './core/guards/admin-role.guard';
import { kontoGuard } from './core/guards/konto.guard';

export const routes: Routes = [
  {
    path: '',
    canActivate: [handwerkerOnboardingGuard],
    loadComponent: () =>
      import('./features/home/home.component').then((m) => m.HomeComponent),
  },
  {
    path: 'suche',
    canActivate: [handwerkerOnboardingGuard],
    loadComponent: () =>
      import('./features/suche/suche.component').then((m) => m.SucheComponent),
  },
  // Static "Preismodell"-Page muss VOR der dynamischen :id-Route stehen,
  // sonst wird "preismodell" als HW-Profil-ID interpretiert.
  {
    path: 'handwerker/preismodell',
    loadComponent: () =>
      import('./features/static-pages/handwerker-preismodell/handwerker-preismodell.component').then(
        (m) => m.HandwerkerPreismodellComponent,
      ),
  },
  {
    path: 'handwerker/:id',
    canActivate: [handwerkerOnboardingGuard],
    loadComponent: () =>
      import('./features/profil/profil.component').then((m) => m.ProfilComponent),
  },
  {
    path: 'buchung/:handwerkerId',
    canActivate: [authGuard, handwerkerOnboardingGuard],
    loadComponent: () =>
      import('./features/buchung/buchung.component').then((m) => m.BuchungComponent),
  },
  {
    path: 'dashboard',
    canActivate: [authGuard, handwerkerRoleGuard],
    loadComponent: () =>
      import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
  },
  // Backend-Notification-Links zeigen auf /cockpit/* (semantische Deep-Links),
  // das Handwerker-Cockpit lebt aber unter /dashboard. Ohne Redirect landen
  // Klicks aus der Inbox/E-Mail über die **-Wildcard auf der Startseite.
  { path: 'cockpit', redirectTo: 'dashboard' },
  { path: 'cockpit/anfragen/:id', redirectTo: 'dashboard' },
  { path: 'cockpit/auftraege/:id', redirectTo: 'dashboard' },
  {
    path: 'benachrichtigungen',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/benachrichtigungen/benachrichtigungen.component').then((m) => m.BenachrichtigungenComponent),
  },
  {
    path: 'dashboard/anfrage/:anfrageId',
    canActivate: [authGuard, endkundeRoleGuard],
    loadComponent: () =>
      import(
        './features/dashboard/kunden-anfrage-detail/kunden-anfrage-detail.component'
      ).then((m) => m.KundenAnfrageDetailComponent),
  },
  {
    path: 'dashboard/anfrage/:anfrageId/bestaetigung',
    canActivate: [authGuard, endkundeRoleGuard],
    loadComponent: () =>
      import(
        './features/dashboard/kunden-buchung-bestaetigung/kunden-buchung-bestaetigung.component'
      ).then((m) => m.KundenBuchungBestaetigungComponent),
  },

  // ── Büro-Automatisierung für Betriebe (Print-Flyer-Zielseite) ──
  // Bare /anfrage gehört der Flyer-Kampagne "Weniger Büro. Mehr Handwerk." —
  // die geführte Kunden-Anfrage lebt darunter unter /anfrage/neu.
  {
    path: 'anfrage',
    loadComponent: () =>
      import('./features/automatisierung/anfrage/automatisierung-anfrage.component').then(
        (m) => m.AutomatisierungAnfrageComponent,
      ),
  },

  // ── Geführte Anfrage (wizard + auswahl + auth-gate + success) ──
  {
    path: 'anfrage/neu',
    loadComponent: () =>
      import('./features/anfrage/wizard/wizard.component').then((m) => m.WizardComponent),
  },
  {
    path: 'anfrage/neu/auswahl',
    loadComponent: () =>
      import('./features/anfrage/auswahl/auswahl.component').then((m) => m.AuswahlComponent),
  },
  {
    path: 'anfrage/neu/anmelden',
    loadComponent: () =>
      import('./features/anfrage/auth-gate/auth-gate.component').then((m) => m.AuthGateComponent),
  },
  {
    path: 'anfrage/neu/success',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/anfrage/success/success.component').then((m) => m.SuccessComponent),
  },

  // ── Auth ──────────────────────────────────────────────────
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/customer-login/customer-login.component').then(
        (m) => m.CustomerLoginComponent,
      ),
  },
  {
    path: 'login/handwerker',
    loadComponent: () =>
      import('./features/auth/handwerker-login/handwerker-login.component').then(
        (m) => m.HandwerkerLoginComponent,
      ),
  },
  {
    path: 'login/success',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/auth/login-success/login-success.component').then(
        (m) => m.LoginSuccessComponent,
      ),
  },
  {
    path: 'registrieren',
    loadComponent: () =>
      import('./features/auth/customer-registrieren/customer-registrieren.component').then(
        (m) => m.CustomerRegistrierenComponent,
      ),
  },
  {
    path: 'registrieren/handwerker',
    loadComponent: () =>
      import('./features/auth/handwerker-registrieren/handwerker-registrieren.component').then(
        (m) => m.HandwerkerRegistrierenComponent,
      ),
  },
  {
    path: 'onboarding',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/onboarding/onboarding.component').then((m) => m.OnboardingComponent),
  },
  {
    path: 'onboarding/success',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/onboarding/onboarding-success.component').then(
        (m) => m.OnboardingSuccessComponent,
      ),
  },
  {
    path: 'auth/callback',
    loadComponent: () =>
      import('./features/auth/auth-callback.component').then((m) => m.AuthCallbackComponent),
  },

  // ── Mein Konto / Settings ─────────────────────────────────
  {
    path: 'konto',
    canActivate: [kontoGuard],
    loadChildren: () =>
      import('./features/konto/konto.routes').then((m) => m.kontoRoutes),
  },

  // ── Customer-Mängelfälle (Complaints-Spec §8.1) ────────────
  {
    path: 'kunde/faelle',
    canActivate: [authGuard, endkundeRoleGuard],
    loadChildren: () =>
      import('./features/faelle/faelle.routes').then((m) => m.faelleRoutes),
  },

  // ── Handwerker-Mängelfälle (Complaints-Spec §8.3) ──────────
  {
    path: 'handwerker/faelle',
    canActivate: [authGuard, handwerkerRoleGuard],
    loadChildren: () =>
      import('./features/hw-faelle/hw-faelle.routes').then((m) => m.hwFaelleRoutes),
  },

  // Konzept § 5 — Profil-Vorschau aus Kundensicht
  {
    path: 'profil/vorschau',
    canActivate: [authGuard, handwerkerRoleGuard],
    loadComponent: () =>
      import('./features/profil/vorschau/profil-vorschau.component').then(
        (m) => m.ProfilVorschauComponent,
      ),
  },

  // ── Rechtliches ───────────────────────────────────────────
  {
    path: 'impressum',
    loadComponent: () =>
      import('./features/rechtliches/impressum.component').then((m) => m.ImpressumComponent),
  },
  {
    path: 'datenschutz',
    loadComponent: () =>
      import('./features/rechtliches/datenschutz.component').then((m) => m.DatenschutzComponent),
  },
  {
    path: 'agb',
    loadComponent: () =>
      import('./features/rechtliches/agb-kunden.component').then((m) => m.AgbKundenComponent),
  },
  {
    path: 'agb/kunden',
    redirectTo: 'agb',
    pathMatch: 'full',
  },
  {
    path: 'agb/handwerker',
    loadComponent: () =>
      import('./features/rechtliches/agb-handwerker.component').then((m) => m.AgbHandwerkerComponent),
  },
  {
    path: 'widerruf',
    loadComponent: () =>
      import('./features/rechtliches/widerruf.component').then((m) => m.WiderrufComponent),
  },
  {
    path: 'cookies',
    loadComponent: () =>
      import('./features/rechtliches/cookies.component').then((m) => m.CookiesComponent),
  },
  // Bewertungs-Richtlinien (Spec § 8.1 — UWG-Transparenzpflicht).
  {
    path: 'bewertungs-richtlinien',
    loadComponent: () =>
      import('./features/rechtliches/bewertungs-richtlinien.component').then(
        (m) => m.BewertungsRichtlinienComponent,
      ),
  },
  // Eigene Bewertungs-Übersicht (Spec § 6.5) — sowohl Kunde als auch Handwerker.
  {
    path: 'bewertungen',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/bewertungen/meine/meine-bewertungen.component').then(
        (m) => m.MeineBewertungenComponent,
      ),
  },
  // Notice & Action (DSA Art. 16) — auch ohne Login erreichbar.
  {
    path: 'melden',
    loadComponent: () =>
      import('./features/melden/melden.component').then((m) => m.MeldenComponent),
  },

  // ── Hilfe-Center ──────────────────────────────────────────
  {
    path: 'hilfe',
    loadChildren: () =>
      import('./features/hilfe/hilfe.routes').then((m) => m.hilfeRoutes),
  },

  // ── Footer-Pages (statisch) ───────────────────────────────
  {
    path: 'ueber-uns',
    loadComponent: () =>
      import('./features/static-pages/ueber-uns/ueber-uns.component').then(
        (m) => m.UeberUnsComponent,
      ),
  },
  {
    path: 'so-funktionierts',
    loadComponent: () =>
      import('./features/static-pages/so-funktionierts/so-funktionierts.component').then(
        (m) => m.SoFunktioniertsComponent,
      ),
  },
  {
    path: 'kontakt',
    loadComponent: () =>
      import('./features/static-pages/kontakt/kontakt.component').then(
        (m) => m.KontaktComponent,
      ),
  },

  // ── Notfall-Modul ─────────────────────────────────────────
  // Public — Express-Wizard ohne Auth-Wall (One-Pager § 2).
  {
    path: 'notfall',
    loadChildren: () =>
      import('./features/notfall/notfall.routes').then((m) => m.notfallRoutes),
  },

  // ── Admin-Backoffice ──────────────────────────────────────
  {
    path: 'admin',
    canActivate: [authGuard, adminRoleGuard],
    loadChildren: () =>
      import('./features/admin/admin.routes').then((m) => m.adminRoutes),
  },

  {
    path: '**',
    redirectTo: '',
  },
];
